import _orderBy from 'lodash/orderBy';

import { genreMatchesFilter, removeNulls } from '@trqk/utils';

interface MapChartTracksToGenresResult {
    data: ChartTrackPerGenre[];
    didFilterSmallValues: boolean;
}

/**
 * Maps an array of `chart_tracks` to a list of objects with a `work_title`, `total_earnings` and an
 * earnings value for each `music_user_genre_name`, like this:
 *
 *  {
 *      work_title: 'Opus No. 9',
 *      total_earnings: 10,
 *      broadcast: 5,   // each `music_user_genre_name` is keyed to its `earnings_contribution` -
 *      digital: 1,     // not every track will contain every `music_user_genre_name`; they are
 *      live: 1,        // only included if they have a value
 *      miscellaneous: 1,
 *      not_reported: 1,
 *      radio: 1,
 *  }
 *
 * Additionally, filters out any tracks with total earnings less than minTrackEarnings and sets a
 * flag if any filtering took place.
 */
export const mapChartTracksToGenres = (
    chartTracks: ProChartTrackWrapper | null,
    genre: BroadcastOrDigital,
    maxSize = 0,
    minValue = 0,
): MapChartTracksToGenresResult => {
    /* eslint-disable no-param-reassign */
    const digitalTracks = removeNulls(
        chartTracks?.digital ?? ([] as ProChartTrack[]),
    );
    const nondigitalTracks = removeNulls(
        chartTracks?.nondigital ?? ([] as ProChartTrack[]),
    );
    const flatTracks = genre === 'digital' ? digitalTracks : nondigitalTracks;
    const mappedData = [] as ChartTrackPerGenre[];
    let didFilterSmallValues = false;

    flatTracks.forEach(
        ({
            work_title,
            music_user_genre_name_contributions,
        }: ProChartTrack): void => {
            const musicUserGenreNameContributions =
                music_user_genre_name_contributions === null
                    ? []
                    : music_user_genre_name_contributions;

            let total_earnings = 0;

            const chartTrackPerGenre = musicUserGenreNameContributions.reduce(
                (
                    acc,
                    {
                        music_user_genre_name,
                        earnings_contribution,
                    }: ProMusicUserGenreNameContribution,
                ): Record<string, Maybe<number>> => {
                    if (music_user_genre_name) {
                        if (
                            earnings_contribution !== undefined &&
                            genreMatchesFilter(music_user_genre_name, genre)
                        ) {
                            acc[music_user_genre_name] = earnings_contribution;

                            if (earnings_contribution !== null) {
                                total_earnings += earnings_contribution;
                            }
                        }
                    }

                    return acc;
                },
                {} as Record<string, Maybe<number>>,
            );

            mappedData.push({
                work_title,
                total_earnings,
                ...chartTrackPerGenre,
            });
        },
    );

    let sortedData: ChartTrackPerGenre[] = _orderBy(
        mappedData,
        ['total_earnings'],
        ['desc'],
    );

    if (maxSize > 0) {
        sortedData = sortedData.slice(0, maxSize);
    }

    const finalData = sortedData.filter(
        ({ total_earnings }: ChartTrackPerGenre): boolean => {
            const result = total_earnings > minValue;

            if (!result) {
                didFilterSmallValues = true;
            }

            return result;
        },
    );

    return { data: finalData, didFilterSmallValues };
    /* eslint-enable no-param-reassign */
};
